import { HttpClient as AngularHttpClient, HttpErrorResponse, HttpXhrBackend } from '@angular/common/http';
import { Bad, BadVal } from '@core/model/framework/Result/BadResult';
import { Ok, OkVal } from '@core/model/framework/Result/OkResult';
import { BadHttpResponse, HttpClient, HttpHeaders, OkHttpResponse } from '@core/model/framework/httpClient';
import { injectable } from 'inversify';
import { firstValueFrom } from 'rxjs';
import { assert } from 'ts-essentials';

@injectable()
export class AngularClient implements HttpClient {
  private httpClient: AngularHttpClient;
  baseUrl: string;

  constructor(baseUrl: string) {
    this.httpClient = new AngularHttpClient(
      new HttpXhrBackend({
        build: () => new XMLHttpRequest(),
      })
    );
    this.baseUrl = baseUrl;
  }

  async post<TResponse, TBody>(
    url: string,
    body: TBody,
    headers?: HttpHeaders
  ): Promise<OkVal<OkHttpResponse<TResponse>> | BadVal<string, BadHttpResponse>> {
    const fullUrl = this.buildUrl(url);
    try {
      const result = await firstValueFrom(
        this.httpClient.post<TResponse>(fullUrl, body, {
          observe: 'response',
          headers,
        })
      );
      assert(result.body, 'httpClient response should be defined.');
      const val: OkHttpResponse<TResponse> = {
        status: result.status,
        data: result.body,
      };
      return Ok(val);
    } catch (error) {
      const httpErrorResponse = error as HttpErrorResponse;
      const val: BadHttpResponse = {
        status: httpErrorResponse.status,
        data: httpErrorResponse.error,
      };
      return Bad('HTTP_ERROR', val);
    }
  }

  async get<TResponse>(
    url: string,
    headers?: HttpHeaders
  ): Promise<OkVal<OkHttpResponse<TResponse>> | BadVal<string, BadHttpResponse>> {
    try {
      const fullUrl = this.buildUrl(url);
      const result = await firstValueFrom(
        this.httpClient.get<TResponse>(fullUrl, {
          observe: 'response',
          headers,
        })
      );
      assert(result.body, 'httpClient response should be defined.');
      const val: OkHttpResponse<TResponse> = {
        status: result.status,
        data: result.body,
      };
      return Ok(val);
    } catch (error) {
      const httpErrorResponse = error as HttpErrorResponse;
      const val: BadHttpResponse = {
        status: httpErrorResponse.status,
        data: httpErrorResponse.error,
      };
      return Bad('HTTP_ERROR', val);
    }
  }

  private buildUrl(url: string): string {
    return `${this.baseUrl}${url}`;
  }
}
